import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Chip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { BlockContainer } from "../../../styles/BlockContainer";
import TextFieldLabeled from "../TextFieldLabeled";

export type AutocompleteLabeledProps<
  T,
  Multiple extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
> = Omit<
  React.ComponentProps<typeof Autocomplete<T, Multiple, FreeSolo, FreeSolo>>,
  "renderInput"
> & {
  label: React.ReactNode;
  placeholder?: string;
  renderTags?: (
    value: T[],
    getTagProps: (params: { index: number }) => Record<string, any>
  ) => React.ReactNode;
};

export function AutocompleteLabeled<
  T,
  Multiple extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false
>(props: AutocompleteLabeledProps<T, Multiple, FreeSolo>) {
  const { label, placeholder, sx, renderTags, ...autoProps } = props;

  return (
    <BlockContainer>
      <Typography
        variant="body2"
        sx={{ mb: 1, fontWeight: 500, color: "hsl(var(--foreground))" }}
      >
        {label}
      </Typography>

      <Autocomplete<T, Multiple, FreeSolo, FreeSolo>
        {...(autoProps as any)}
        sx={{
          "& .MuiInputBase-root": {
            border: "1px solid hsl(var(--border))",
            borderRadius: "6px",
            py: "2px", // Réduire le padding vertical pour une hauteur de 36px
            px: "10px",
            minHeight: "36px", // Hauteur fixe du composant
            bgcolor: "hsl(var(--background))",
            color: "hsl(var(--foreground))",
          },
          "& .MuiInputBase-input": {
            padding: "0", // <-- padding interne du champ
            color: "hsl(var(--foreground))",
          },
          "& .MuiAutocomplete-tag": {
            height: "24px", // Hauteur des chips
            margin: "2px", // Petit margin pour l'espacement
            bgcolor: "hsl(var(--muted))",
            color: "hsl(var(--foreground))",
            "& .MuiChip-deleteIcon": {
              color: "hsl(var(--muted-foreground))",
              "&:hover": {
                color: "hsl(var(--destructive))",
              },
            },
          },
          "& .MuiInputBase-root:focus-within": {
            borderColor: "hsl(var(--primary))",
            borderWidth: "2px",
          },
          "& .MuiAutocomplete-popupIndicator": {
            color: "hsl(var(--muted-foreground))",
          },
          "& .MuiAutocomplete-clearIndicator": {
            color: "hsl(var(--muted-foreground))",
          },
          ...sx,
        }}
        renderInput={(params: AutocompleteRenderInputParams) => {
          const { value, onChange, ...restInputProps } =
            params.inputProps as React.HTMLAttributes<HTMLInputElement> & {
              value: unknown;
              onChange: React.ChangeEventHandler<HTMLInputElement>;
            };

          return (
            <TextFieldLabeled
              label=""
              multiline
              minRows={1}
              placeholder={placeholder}
              value={value as string}
              onChange={onChange}
              inputProps={restInputProps}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true, // très important pour éviter double bordure
              }}
            />
          );
        }}
        renderTags={
          renderTags
            ? renderTags
            : (value, getTagProps) =>
                value.map((option, index) => {
                  const tagProps = getTagProps({ index });
                  const { key, ...other } = tagProps;
                  return (
                    <Chip
                      key={key}
                      {...other}
                      label={String(option)}
                      sx={{
                        bgcolor: "hsl(var(--muted))",
                        color: "hsl(var(--foreground))",
                        "& .MuiChip-deleteIcon": {
                          color: "hsl(var(--muted-foreground))",
                          "&:hover": {
                            color: "hsl(var(--destructive))",
                          },
                        },
                      }}
                    />
                  );
                })
        }
      />
    </BlockContainer>
  );
}
